import { MeetingSessionOvenMediaReceiverManagerBaseData, useMeetingSessionOvenMediaReceiverManager } from "@kalyzee/kast-web-sdk";
import { ChatRef, QRCodeHooks, useRefAutoUpdate, useStateWithRef } from "@kalyzee/kast-web-sdk/web-components";
import {
  MeetingRoom,
  MeetingRoomHighlightingState,
  MeetingRoomStatus,
  MeetingSession,
  MeetingSessionHighlightingState,
  MeetingSessionMedia,
  MeetingState,
} from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PageSlaveMode } from "../../../components/navigation/PageContext";
import PauseView from "../../../components/utils/PauseView";
import { CarouselData } from "../../../helpers/bridge/common";
import { useMeetingSlaveMessagingBridge } from "../../../hooks/secondaryScreens/messagingBridge";
import { useScreen } from "../../../hooks/secondaryScreens/screen";
import styles from "./meeting.module.css";
import { SecondaryScreenLayout } from "../../../interfaces/secondaryScreens";
import { PlayerGridWorkBox } from "../../../components/workBoxes/GridWorkBox";
import { BaseTopBar } from "../../../components/common/BaseTopBar";
import { ChatWorkBox } from "../../../components/workBoxes/ChatWorkBox";
import SlaveChat from "../../../components/chat/SlaveChat";
import { ScreenHelpers } from "../../../helpers/screen";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";

export interface MeetingSlavePageProps {}

type SlaveCarouselData = {
  data: CarouselData;
  page: number;
  pageCount: number;
};

export const useGroupRequest = (rooms?: MeetingRoom[]) => {
  const [requests, setRequests, requestsRef] = useStateWithRef<{type: "highlight" | "callingModerator", room: MeetingRoom}[]>([]);
  const groupHighlighRequests = useMemo(() => rooms?.filter((r) => r.status !== MeetingRoomStatus.Closed && r.highlighting === MeetingRoomHighlightingState.PENDING_REQUEST) ?? [], [rooms]);
  const groupModeratorRequests = useMemo(() => rooms?.filter((r) => r.status !== MeetingRoomStatus.Closed && r.callingModerator) ?? [], [rooms]);

  useEffect(() => {
    let update = false;
    const newRequests: {type: "highlight" | "callingModerator", room: MeetingRoom}[] = [];
    requestsRef.current.forEach((r) => {
      if (r.type === "highlight") {
        if (groupHighlighRequests.find((r2) => r.room.id === r2.id)) {
          newRequests.push(r);
        } else {
          update = true;
        }
      } else if (r.type === "callingModerator") {
        if (groupModeratorRequests.find((r2) => r.room.id === r2.id)) {
          newRequests.push(r);
        } else {
          update = true;
        }
      }
    });

    groupHighlighRequests.forEach((r) => {
      if (!requestsRef.current.find((r2) => r2.room.id === r.id)) {
        newRequests.push({type: "highlight", room: r});
        update = true;
      }
    });

    groupModeratorRequests.forEach((r) => {
      if (!requestsRef.current.find((r2) => r2.room.id === r.id)) {
        newRequests.push({type: "callingModerator", room: r});
        update = true;
      }
    });

    if (update) setRequests(newRequests);
  }, [groupHighlighRequests, groupModeratorRequests]);

  return requests;
};

const MeetingSlavePage = ({}: MeetingSlavePageProps) => {
  const chatRef = useRef<ChatRef>(null);
  const { bridge, meeting, session } = useMeetingSlaveMessagingBridge(chatRef);
  const bridgeRef = useRefAutoUpdate(bridge);

  const { screen, layout } = useScreen();

  const [data, setData] = useState<SlaveCarouselData>({ page: 0, pageCount: 0, data: { medias: [], totalSessions: 0 } });
  const [mediasToPreload, setMediasToPreload] = useState<MeetingSessionMedia[]>([]);

  const highlightRequests = useMemo(
    () => meeting?.sessions?.filter((s) => s.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST) ?? [],
    [meeting]
  );

  const { manager: receptionManager, sessions: videoSessions } = useMeetingSessionOvenMediaReceiverManager(
    meeting,
    useCallback(
      (curr: MeetingSession, media: MeetingSessionMedia) => {
        //if (cleanedScreen === 2 && highlightRequests.find((s) => s.id === curr.id)) return true;
        if (mediasToPreload.find((m) => m.id === media.id)) return true;
        return data.data.medias.some((m) => m.id === media.id);
      },
      [data, mediasToPreload, highlightRequests]
    )
  );

  const timeoutDisplaySessionsRef = useRef<NodeJS.Timeout>();

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const clean = () => {
    bridgeRef.current?.postClearClientData(PageSlaveMode.MEETING);
    clearTimeout(timeoutDisplaySessionsRef.current);
  };

  // ---------------------------------------------------------------------- //
  // --------------------------- EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    bridgeRef.current?.postClientData(PageSlaveMode.MEETING, {
      layoutId: layout.id,
      videoGroupId: screen?.videoGroupId,
      // players: screenParams.rows * screenParams.columns,
      // groups: groupSlots,
    });
  }, [screen, meeting]);

  useEffect(() => {
    if (meeting?.state === MeetingState.RUNNING) {
      receptionManager.restore();
    } else {
      receptionManager.pause();
    }
  }, [meeting, receptionManager]);

  useEffect(() => {
    if (!bridge) return;
    const displayCarouselListener = bridge.addEventListener("displayCarousel", (msg) => {
      const { data, delay, page, pageCount } = msg.content;
      if (delay <= 0) {
        clearTimeout(timeoutDisplaySessionsRef.current);
        setMediasToPreload([]);
        setData({ page, pageCount, data });
      } else {
        setMediasToPreload(data.medias);
        timeoutDisplaySessionsRef.current = setTimeout(() => {
          setMediasToPreload([]);
          setData({ page, pageCount, data });
        }, delay);
      }
    });
    return () => {
      displayCarouselListener.flush();
    };
  }, [bridge]);

  useEffect(() => {
    if (!meeting || !session) clean();
  }, [meeting, session]);

  useEffect(() => {
    return () => {
      clean();
    };
  }, []);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER --------------------------------- //
  // ---------------------------------------------------------------------- //

  const renderVideoColumn = (
    cols: number,
    rows: number,
    medias: MeetingSessionMedia[],
    boxMode?: boolean,
  ) => {
    if (!meeting || !session) return null;
    const columnSessions = medias
      .map(media => videoSessions.find(s => s.data.mediaSession.id === media.id))
      .filter(s => !!s);
    return (
      <div className={styles.column} style={{ flex: 1 }}>
        <PlayerGridWorkBox
          meeting={meeting}
          ownSession={session}
          videoSessions={columnSessions as OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData<any>>[]}
          columns={cols}
          rows={rows}
          boxMode={boxMode}
          gap={5}
          label={`${data.data.totalSessions} Participant${data.data.totalSessions > 1 ? "s" : ""}`}
        />
      </div>
    );
  }

  const renderChatColumn = () => {
    return (
      <ChatWorkBox>
        <SlaveChat
          chatRef={chatRef}
        />
      </ChatWorkBox>
    );
  }


  const renderLayoutColumns = () => {
    const nodes: React.ReactNode[] = [];
    
    // We separate the players columns but we keep them bound to their original position
    const indexedPlayerColumns = layout.columns
      .map((c, i) => ({ ...c, index: i }))
      .filter(c => c.contentType === "players"); 

    // The columns with a content priority are the first to eat
    const playerColumnsByPriority = indexedPlayerColumns.slice().sort((c1, c2) => {
      const p1 = c1.playerContentPriority ? 1 : 0;
      const p2 = c2.playerContentPriority ? 1 : 0;
      return p2 - p1;
    });

    //
    const mediaByColumn: { [index: number]: MeetingSessionMedia[] } = {};
    let remainingMedia = data.data.medias.slice();
    
    playerColumnsByPriority.forEach(col => {
      const dim = ScreenHelpers.getColumnPlayers(col);
      const capacity = dim.columns * dim.rows;

      let collected: MeetingSessionMedia[] = [];
      
      const contentPrio = col.playerContentPriority;
      if (contentPrio) {
        if (contentPrio.target === "name") {
          const filtered = remainingMedia.filter(media => {
            const name = media.name ?? "";
            return name.toLowerCase().includes(contentPrio.value.toLowerCase());
          });
          collected = filtered.slice(0, capacity);
          remainingMedia = remainingMedia.filter(s => !collected.includes(s));
        }
      }

      if (collected.length < capacity) {
        const toTake = capacity - collected.length;
        collected = collected.concat(remainingMedia.slice(0, toTake));
        remainingMedia = remainingMedia.slice(toTake);
      }

      mediaByColumn[col.index] = collected;
    });

    const renderColumn = (column: SecondaryScreenLayout["columns"][0]) => {
      if (column.contentType === "players") {
        const dim = ScreenHelpers.getColumnPlayers(column);
        const idx = layout.columns.indexOf(column);
        const columnMedia = mediaByColumn[idx] || [];
        return renderVideoColumn(
          dim.columns,
          dim.rows,
          columnMedia,
          column.playerBoxMode,
        );
      } else if (column.contentType === "chat") {
        return renderChatColumn();
      } else {
        return <>Unimplemented column type</>
      }
    }

    layout.columns.forEach((column, idx) => {
      nodes.push(
        <div style={{ height: "100%", width: `${column.width * 25}%` }} key={idx}>
          {renderColumn(column)}
        </div>
      )
    });

    return (
      <div style={{ 
        height: "100%", 
        width: "100%", 
        display: "flex",
        gap: "1em",
        justifyContent: layout.justifyContent,
      }}>
        {nodes}
      </div>
    );
  };

  const renderTopBar = () => {
    const topBarOptions = layout.topBar;
    if (!topBarOptions) return null;
    return (
      <BaseTopBar
        showLogo={topBarOptions.brand}
        watchingNumber={topBarOptions.watchers ? meeting?.sessions?.length : undefined}
        user={topBarOptions.user ? session?.user : undefined}
        meetingName={topBarOptions.meetingName ? meeting?.name : undefined}
        meetingStartedAt={topBarOptions.timer ? meeting?.startedAt : undefined}
      />
    );
  };

  const renderPager = () => (
    data.pageCount > 1 ? (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "20px",
          padding: "10px 10px",
          backgroundColor: "#88888866",
          margin: "10px 0px",
          gap: "5px",
        }}
      >
        {new Array(data.pageCount).fill(0).map((_, i) => {
          return (
            <div
              key={`page_${i}`}
              style={{ width: "6px", height: "6px", borderRadius: "100%", backgroundColor: i === data.page ? "#CCCCCCDD" : "#AAAAAA44" }}
            />
          );
        })}
      </div>
    ) : null
  );

  const renderContent = () => {
    if (!meeting || !session) {
      const screenName = screen?.name ?? (screen?.index !== undefined ? `Écran n°${screen.index}` : undefined);
      return (
        <div className={styles.waiting}>
          <div className={styles.content}>En attente d'un événement ...</div>
          {screenName && <div className={styles.footer}>
            {screenName}
          </div>}
        </div>
      );
    }
    return (
      <>
        <div style={{
          display: "flex",
          width: "100%",
          flex: 1,
          minHeight: 0,
        }}>
          {renderLayoutColumns()}
        </div>
        {renderPager()}
      </>
    );
  };

  return (
    <PauseView 
      meeting={meeting}
      style={{ display: "flex" }}
    >
      <div className={styles.container}>
        {renderTopBar()}
        <div style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          flex: 1,
          minHeight: 0,
          padding: "1em",
        }}>
          {renderContent()}
        </div>
      </div>
    </PauseView>
  );
};

export default MeetingSlavePage;
